import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowLeft, Loader2, AlertTriangle, Award, CheckCircle2, Handshake } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateJoin = () => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/profile', { replace: true });
  }, [user, navigate]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/affiliate/register`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
      }, { withCredentials: true });
      sessionStorage.setItem('token', data.access_token);
      sessionStorage.setItem('user', JSON.stringify(data.user));
      await checkAuth();
      toast.success('Welcome to the Affiliate Hub!');
      // Persona verification required before reaching Verified Partner Page
      navigate('/verification', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not create your account. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[42%] relative bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 text-white flex-col p-12 overflow-hidden">
        <div className="absolute -top-20 -right-10 w-80 h-80 bg-white/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-10 w-96 h-96 bg-yellow-300/20 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-15">
          <img src="https://images.unsplash.com/photo-1605256585681-455837661b18?auto=format&fit=crop&w=1200&q=70" alt="" className="w-full h-full object-cover" />
        </div>

        <div className="relative z-10">
          <Link to="/affiliate-hub" className="inline-flex items-center gap-2.5 mb-10">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-9 w-auto" />
            <span className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 border border-white/30 backdrop-blur rounded-full text-xs font-semibold mb-6 tracking-wider uppercase">
            <Award className="h-3.5 w-3.5" /> Become a Verified Partner
          </div>
          <h2 className="text-3xl font-bold leading-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Join the trusted box truck marketplace.</h2>
          <p className="text-orange-50 text-sm leading-relaxed mb-8 max-w-md">
            Real Resources. Real Earnings. Build referral revenue while connecting with verified carriers across the country.
          </p>

          <div className="space-y-3 max-w-md">
            {[
              'Access a verified driver & carrier network',
              'List your services in the Featured Categories',
              'Earn payouts on qualified referrals',
              'Spotlighted in our Member Partner showcase',
            ].map(item => (
              <div key={item} className="flex items-start gap-3 bg-white/10 border border-white/20 rounded-lg p-3 backdrop-blur">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 mt-auto pt-8">
          <div className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-lg p-4 backdrop-blur max-w-sm">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center"><Handshake className="h-5 w-5 text-orange-600" /></div>
            <div>
              <p className="text-xs font-bold">Straight Connect Verified</p>
              <p className="text-[11px] text-orange-50">Reviewed by our specialist team</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 overflow-y-auto bg-white">
        <div className="w-full max-w-[480px]">
          <Link to="/affiliate-hub" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors" data-testid="affiliate-join-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Affiliate Hub
          </Link>

          <div className="lg:hidden mb-6 flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="Logo" className="h-8 w-auto" />
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>StraightConnect</span>
          </div>

          <span className="inline-block text-xs font-semibold text-orange-600 uppercase tracking-wider mb-2">Affiliate Sign Up</span>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Create your partner account</h2>
          <p className="text-slate-500 text-sm mb-6">After signup, you&apos;ll verify your identity and submit your Verified Partner application.</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3.5 mb-5 flex items-start gap-3" data-testid="affiliate-join-error">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="affiliate-join-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1.5">First Name</label>
                <input required value={form.first_name} onChange={(e) => update('first_name', e.target.value)}
                  className="w-full h-11 px-4 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="affiliate-join-first-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1.5">Last Name</label>
                <input required value={form.last_name} onChange={(e) => update('last_name', e.target.value)}
                  className="w-full h-11 px-4 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="affiliate-join-last-name" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Email Address</label>
              <input type="email" required value={form.email} onChange={(e) => update('email', e.target.value)}
                placeholder="partner@company.com"
                className="w-full h-11 px-4 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="affiliate-join-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Phone Number</label>
              <input type="tel" required value={form.phone} onChange={(e) => update('phone', e.target.value)}
                placeholder="(555) 555-1234"
                className="w-full h-11 px-4 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="affiliate-join-phone" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} required minLength={6} value={form.password} onChange={(e) => update('password', e.target.value)}
                  className="w-full h-11 px-4 pr-12 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="affiliate-join-password" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="affiliate-join-toggle-pw">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">Confirm Password</label>
              <input type={showPw ? 'text' : 'password'} required value={form.confirm_password} onChange={(e) => update('confirm_password', e.target.value)}
                className="w-full h-11 px-4 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="affiliate-join-confirm-password" />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} className="rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
              Show password
            </label>

            <button type="submit" disabled={loading}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 mt-4" data-testid="affiliate-join-submit">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Creating account...</> : 'Sign Up Affiliate'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already a partner?{' '}
            <Link to="/affiliate/login" className="font-semibold text-orange-600 hover:text-orange-700 hover:underline" data-testid="affiliate-join-signin-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateJoin;
